/**
 * Google Calendar Integration Service
 * Handles OAuth authentication, event fetching, and event creation for Google Calendar
 */

import { db } from './firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { GoogleCalendarConfig, GoogleCalendarEvent, GoogleCalendarList, Activity } from '../types';
import { getSettings, updateSetting } from './appSettings';

// Google Calendar API endpoints
const GOOGLE_CALENDAR_API = 'https://www.googleapis.com/calendar/v3';
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';

// OAuth scopes needed
const SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/userinfo.email',
];

// Get the OAuth Client ID from settings
let cachedClientId: string | null = null;

export const getGoogleClientId = async (): Promise<string> => {
  if (cachedClientId) return cachedClientId;
  
  const settings = await getSettings();
  cachedClientId = settings.googleCalendarClientId || 
                   process.env.REACT_APP_GOOGLE_CALENDAR_CLIENT_ID || 
                   '';
  return cachedClientId;
};

export const setGoogleClientId = async (clientId: string): Promise<void> => {
  cachedClientId = clientId;
  await updateSetting('googleCalendarClientId', clientId);
};

// ==========================================
// USER GOOGLE CALENDAR CONFIG
// ==========================================

/**
 * Get user's Google Calendar configuration
 */
export const getUserGoogleCalendarConfig = async (userId: string): Promise<GoogleCalendarConfig | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      if (data.googleCalendar) {
        // Convert Firestore timestamp to Date
        const config = data.googleCalendar as GoogleCalendarConfig;
        if (config.tokenExpiry && typeof config.tokenExpiry === 'object' && 'toDate' in config.tokenExpiry) {
          config.tokenExpiry = (config.tokenExpiry as any).toDate();
        }
        if (config.lastSyncAt && typeof config.lastSyncAt === 'object' && 'toDate' in config.lastSyncAt) {
          config.lastSyncAt = (config.lastSyncAt as any).toDate();
        }
        return config;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting Google Calendar config:', error);
    return null;
  }
};

/**
 * Save user's Google Calendar configuration
 */
export const saveUserGoogleCalendarConfig = async (
  userId: string, 
  config: Partial<GoogleCalendarConfig>
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const currentConfig = await getUserGoogleCalendarConfig(userId);
    
    await updateDoc(userRef, {
      googleCalendar: {
        ...currentConfig,
        ...config,
      }
    });
  } catch (error) {
    console.error('Error saving Google Calendar config:', error);
    throw error;
  }
};

// ==========================================
// OAUTH AUTHENTICATION
// ==========================================

/**
 * Generate OAuth URL for Google Calendar authorization
 */
export const getGoogleAuthUrl = async (redirectUri: string): Promise<string> => {
  const clientId = await getGoogleClientId();
  if (!clientId) {
    throw new Error('Google Calendar Client ID not configured');
  }
  
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: SCOPES.join(' '),
    access_type: 'offline',
    prompt: 'consent', // Force consent screen to get refresh token
  });
  
  return `${GOOGLE_AUTH_URL}?${params.toString()}`;
};

/**
 * Exchange authorization code for tokens
 * Note: This should ideally be done server-side for security
 */
export const exchangeCodeForTokens = async (
  code: string,
  redirectUri: string
): Promise<{
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  email?: string;
}> => {
  const clientId = await getGoogleClientId();
  
  // For client-side OAuth, we use implicit flow or need to handle this server-side
  // In a production app, this should be a Cloud Function
  const response = await fetch(GOOGLE_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      code,
      client_id: clientId,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code',
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error_description || 'Failed to exchange code for tokens');
  }
  
  const data = await response.json();
  
  // Get user email
  let email: string | undefined;
  try {
    const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: { Authorization: `Bearer ${data.access_token}` },
    });
    if (userInfoResponse.ok) {
      const userInfo = await userInfoResponse.json();
      email = userInfo.email;
    }
  } catch (e) {
    console.warn('Could not fetch user email:', e);
  }
  
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
    email,
  };
};

/**
 * Connect Google Calendar for a user
 */
export const connectGoogleCalendar = async (
  userId: string,
  code: string,
  redirectUri: string
): Promise<void> => {
  const tokens = await exchangeCodeForTokens(code, redirectUri);
  
  const config: GoogleCalendarConfig = {
    connected: true,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    tokenExpiry: new Date(Date.now() + tokens.expiresIn * 1000),
    selectedCalendars: ['primary'], // Default to primary calendar
    email: tokens.email,
  };
  
  await saveUserGoogleCalendarConfig(userId, config);
};

/**
 * Disconnect Google Calendar for a user
 */
export const disconnectGoogleCalendar = async (userId: string): Promise<void> => {
  const config = await getUserGoogleCalendarConfig(userId);
  
  // Revoke access if we have a token
  if (config?.accessToken) {
    try {
      await fetch(`https://oauth2.googleapis.com/revoke?token=${config.accessToken}`, {
        method: 'POST',
      });
    } catch (e) {
      console.warn('Error revoking token:', e);
    }
  }
  
  // Clear the config
  await saveUserGoogleCalendarConfig(userId, {
    connected: false,
    accessToken: undefined,
    refreshToken: undefined,
    tokenExpiry: undefined,
    selectedCalendars: [],
    email: undefined,
    lastSyncAt: undefined,
  });
};

/**
 * Repair stale state where tokens exist but connected was cleared (e.g. failed client-side refresh).
 */
export const repairGoogleCalendarConnectionState = async (userId: string): Promise<boolean> => {
  const config = await getUserGoogleCalendarConfig(userId);
  if (config?.refreshToken && config.connected !== true) {
    await saveUserGoogleCalendarConfig(userId, { connected: true });
    return true;
  }
  return false;
};

/**
 * Check if token needs refresh and refresh if needed
 */
export const ensureValidToken = async (userId: string): Promise<string | null> => {
  await repairGoogleCalendarConnectionState(userId);
  const config = await getUserGoogleCalendarConfig(userId);
  if ((!config?.connected && !config?.refreshToken) || !config.accessToken) {
    if (config?.refreshToken && !config.accessToken) {
      /* fall through to server refresh */
    } else {
      return null;
    }
  }
  
  // Check if token is expired or will expire in the next 5 minutes
  const expiryBuffer = 5 * 60 * 1000; // 5 minutes
  const isExpired = !config?.accessToken || (config.tokenExpiry && 
                    new Date(config.tokenExpiry).getTime() < Date.now() + expiryBuffer);
  
  if (!isExpired && config?.accessToken) {
    return config.accessToken;
  }
  
  // Token needs refresh
  if (!config?.refreshToken) {
    console.error('No refresh token available');
    return null;
  }
  
  try {
    const functions = getFunctions(undefined, 'us-central1');
    const refresh = httpsCallable(functions, 'refreshGoogleToken');
    const result = await refresh({});
    const data = result.data as { accessToken: string; expiresIn: number };
    
    // Update stored token
    await saveUserGoogleCalendarConfig(userId, {
      accessToken: data.accessToken,
      tokenExpiry: new Date(Date.now() + data.expiresIn * 1000),
      connected: true,
    });
    
    return data.accessToken;
  } catch (error) {
    console.error('Error refreshing token:', error);
    await disconnectGoogleCalendar(userId);
    return null;
  }
};

// ==========================================
// CALENDAR OPERATIONS
// ==========================================

/**
 * List user's calendars
 */
export const listCalendars = async (userId: string): Promise<GoogleCalendarList[]> => {
  const accessToken = await ensureValidToken(userId);
  if (!accessToken) {
    throw new Error('Not authenticated with Google Calendar');
  }
  
  const response = await fetch(`${GOOGLE_CALENDAR_API}/users/me/calendarList`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch calendars');
  }
  
  const data = await response.json();
  
  return (data.items || []).map((cal: any) => ({
    id: cal.id,
    summary: cal.summary,
    description: cal.description,
    primary: cal.primary,
    backgroundColor: cal.backgroundColor,
    foregroundColor: cal.foregroundColor,
    accessRole: cal.accessRole,
  }));
};

/**
 * Fetch calendar events
 */
export const fetchCalendarEvents = async (
  userId: string,
  calendarIds: string[],
  startDate: Date,
  endDate: Date
): Promise<GoogleCalendarEvent[]> => {
  const accessToken = await ensureValidToken(userId);
  if (!accessToken) {
    throw new Error('Not authenticated with Google Calendar');
  }
  
  const allEvents: GoogleCalendarEvent[] = [];
  
  for (const calendarId of calendarIds) {
    try {
      const params = new URLSearchParams({
        timeMin: startDate.toISOString(),
        timeMax: endDate.toISOString(),
        singleEvents: 'true',
        orderBy: 'startTime',
        maxResults: '250',
      });
      
      const response = await fetch(
        `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      if (!response.ok) {
        console.error(`Failed to fetch events for calendar ${calendarId}`);
        continue;
      }
      
      const data = await response.json();
      
      const events = (data.items || []).map((event: any) => ({
        id: event.id,
        calendarId,
        title: event.summary || '(No title)',
        description: event.description,
        start: event.start.dateTime 
          ? new Date(event.start.dateTime) 
          : new Date(event.start.date),
        end: event.end.dateTime 
          ? new Date(event.end.dateTime) 
          : new Date(event.end.date),
        location: event.location,
        htmlLink: event.htmlLink,
        isAllDay: !event.start.dateTime,
        colorId: event.colorId,
        status: event.status,
        attendees: event.attendees?.map((a: any) => ({
          email: a.email,
          displayName: a.displayName,
          responseStatus: a.responseStatus,
        })),
      }));
      
      allEvents.push(...events);
    } catch (error) {
      console.error(`Error fetching events for calendar ${calendarId}:`, error);
    }
  }
  
  // Update last sync time
  await saveUserGoogleCalendarConfig(userId, { lastSyncAt: new Date() });
  
  // Sort by start time
  return allEvents.sort((a, b) => a.start.getTime() - b.start.getTime());
};

// ==========================================
// EVENT CRUD OPERATIONS
// ==========================================

/**
 * Create a Google Calendar event from a CRM activity
 */
export const createGoogleEvent = async (
  userId: string,
  calendarId: string,
  activity: Activity
): Promise<string> => {
  const accessToken = await ensureValidToken(userId);
  if (!accessToken) {
    throw new Error('Not authenticated with Google Calendar');
  }
  
  const startTime = activity.scheduledAt || new Date();
  const endTime = new Date(startTime.getTime() + (activity.duration || 60) * 60 * 1000);
  
  const event = {
    summary: activity.title,
    description: [
      activity.description,
      '',
      `CRM Activity: ${activity.type}`,
      activity.entityName ? `Related to: ${activity.entityName}` : '',
    ].filter(Boolean).join('\n'),
    start: {
      dateTime: startTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    end: {
      dateTime: endTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  };
  
  const response = await fetch(
    `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to create Google Calendar event');
  }
  
  const createdEvent = await response.json();
  return createdEvent.id;
};

export type InstantMeetEventResult = {
  eventId: string;
  calendarId: string;
  meetUrl: string;
  start: Date;
  end: Date;
  dialInPin?: string;
};

function parseMeetUrlFromCalendarEvent(event: Record<string, unknown>): string {
  const hangout = event.hangoutLink;
  if (typeof hangout === 'string' && hangout.trim()) return hangout.trim();
  const conf = event.conferenceData as { entryPoints?: Array<{ entryPointType?: string; uri?: string; pin?: string }> } | undefined;
  const video = conf?.entryPoints?.find((e) => e.entryPointType === 'video' && e.uri);
  if (video?.uri) return video.uri;
  const any = conf?.entryPoints?.find((e) => e.uri);
  if (any?.uri) return any.uri;
  throw new Error('Google Calendar did not return a Meet link');
}

function parseDialInPinFromCalendarEvent(event: Record<string, unknown>): string | undefined {
  const conf = event.conferenceData as { entryPoints?: Array<{ entryPointType?: string; pin?: string }> } | undefined;
  const phone = conf?.entryPoints?.find((e) => e.entryPointType === 'phone' && e.pin);
  return phone?.pin ? String(phone.pin) : undefined;
}

/**
 * Creates a short instant Calendar event with a Google Meet link (comment quick meeting).
 */
export const createInstantMeetEvent = async (
  userId: string,
  params: {
    title: string;
    description?: string;
    durationMinutes?: number;
    calendarId?: string;
  },
): Promise<InstantMeetEventResult> => {
  const accessToken = await ensureValidToken(userId);
  if (!accessToken) {
    throw new Error('Not authenticated with Google Calendar');
  }

  const calendarId = params.calendarId ?? (await getDefaultCalendarForEvents(userId));
  const durationMinutes = params.durationMinutes ?? 30;
  const startTime = new Date();
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const event = {
    summary: params.title,
    description: params.description || '',
    start: {
      dateTime: startTime.toISOString(),
      timeZone,
    },
    end: {
      dateTime: endTime.toISOString(),
      timeZone,
    },
    conferenceData: {
      createRequest: {
        requestId,
        conferenceSolutionKey: { type: 'hangoutsMeet' },
      },
    },
  };

  const response = await fetch(
    `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events?conferenceDataVersion=1`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      (error as { error?: { message?: string } })?.error?.message
        || 'Failed to create Google Meet event',
    );
  }

  const createdEvent = (await response.json()) as Record<string, unknown>;
  const meetUrl = parseMeetUrlFromCalendarEvent(createdEvent);
  const dialInPin = parseDialInPinFromCalendarEvent(createdEvent);

  return {
    eventId: String(createdEvent.id || ''),
    calendarId,
    meetUrl,
    start: startTime,
    end: endTime,
    dialInPin,
  };
};

/**
 * Update a Google Calendar event
 */
export const updateGoogleEvent = async (
  userId: string,
  calendarId: string,
  eventId: string,
  activity: Activity
): Promise<void> => {
  const accessToken = await ensureValidToken(userId);
  if (!accessToken) {
    throw new Error('Not authenticated with Google Calendar');
  }
  
  const startTime = activity.scheduledAt || new Date();
  const endTime = new Date(startTime.getTime() + (activity.duration || 60) * 60 * 1000);
  
  const event = {
    summary: activity.title,
    description: [
      activity.description,
      '',
      `CRM Activity: ${activity.type}`,
      activity.entityName ? `Related to: ${activity.entityName}` : '',
    ].filter(Boolean).join('\n'),
    start: {
      dateTime: startTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    end: {
      dateTime: endTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  };
  
  const response = await fetch(
    `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events/${eventId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to update Google Calendar event');
  }
};

/**
 * Delete a Google Calendar event
 */
export const deleteGoogleEvent = async (
  userId: string,
  calendarId: string,
  eventId: string
): Promise<void> => {
  const accessToken = await ensureValidToken(userId);
  if (!accessToken) {
    throw new Error('Not authenticated with Google Calendar');
  }
  
  const response = await fetch(
    `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events/${eventId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  
  if (!response.ok && response.status !== 404) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to delete Google Calendar event');
  }
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Check if user has Google Calendar connected
 */
export const isGoogleCalendarConnected = async (userId: string): Promise<boolean> => {
  await repairGoogleCalendarConnectionState(userId);
  const config = await getUserGoogleCalendarConfig(userId);
  return !!config?.refreshToken && (!!config.accessToken || config.connected === true);
};

/**
 * Get user's selected calendars
 */
export const getSelectedCalendars = async (userId: string): Promise<string[]> => {
  const config = await getUserGoogleCalendarConfig(userId);
  return config?.selectedCalendars || [];
};

/**
 * Update user's selected calendars
 */
export const updateSelectedCalendars = async (
  userId: string,
  calendarIds: string[]
): Promise<void> => {
  await saveUserGoogleCalendarConfig(userId, { selectedCalendars: calendarIds });
};

/**
 * Get the default calendar for creating events (first selected or primary)
 */
export const getDefaultCalendarForEvents = async (userId: string): Promise<string> => {
  const config = await getUserGoogleCalendarConfig(userId);
  if (config?.selectedCalendars && config.selectedCalendars.length > 0) {
    return config.selectedCalendars[0];
  }
  return 'primary';
};
